/**
 * Next consolidated-invoice cutoffs (#3933).
 *
 * Query params are the unsaved billing form. When cycle, weekday and cutoff
 * time are all present they override the saved tenant schedule; an optional
 * biweekly anchor is sent only when the form has a Monday.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ConsolidationSchedulePreviewParams } from '@/features/settings/consolidatedInvoicingForm';

export interface ConsolidationScheduleCutoff {
  cutoffAt: string;
  issueDate: string;
  kind: 'regular' | 'month_end';
}

export interface ConsolidationSchedulePreview {
  enabled: boolean;
  cutoffs: ConsolidationScheduleCutoff[];
}

function previewSearchParams(
  params: ConsolidationSchedulePreviewParams,
): Record<string, string | number | boolean | undefined> {
  const query: Record<string, string | number | boolean | undefined> = {
    cycle: params.cycle,
    weekday: params.weekday,
    cutoffTime: params.cutoffTime,
  };
  if (params.biweeklyAnchorWeek !== undefined) {
    query['biweeklyAnchorWeek'] = params.biweeklyAnchorWeek;
  }
  return query;
}

export function useConsolidationSchedulePreview(params: ConsolidationSchedulePreviewParams | null) {
  return useQuery({
    queryKey: ['invoices', 'consolidation-schedule-preview', params],
    enabled: params !== null,
    queryFn: () => {
      if (params === null) {
        return Promise.reject(new Error('Consolidation schedule preview is disabled'));
      }
      return api.get<ConsolidationSchedulePreview>('/invoices/consolidation/schedule-preview', {
        params: previewSearchParams(params),
      });
    },
  });
}
